import { decodeJwt } from 'jose'
import { create } from 'zustand'
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware'

import { request } from '@/lib/utils/request'

type AuthStore = {
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => Promise<void>
  authenticate: (accessToken: string, refreshToken: string) => Promise<void>
  isLoading: boolean
  isInitializing: boolean
  initialize: () => Promise<void>
  isAuthenticated: boolean
  accessToken: string | null
  refreshToken: string | null
  rememberMe: boolean
}

const EXPIRY_SOON_OFFSET = 10 * 60 // 10 minutes in seconds
const MAX_RETRIES = 5
const RETRY_INTERVAL_MS = 15 * 1000
let refreshTimeoutId: NodeJS.Timeout | null = null

const loginStorage: StateStorage = {
  getItem(name: string): string | null {
    if (sessionStorage.getItem(name)) {
      return sessionStorage.getItem(name)
    } else {
      return localStorage.getItem(name)
    }
  },

  setItem(name: string, value: string): void {
    if (sessionStorage.getItem(name)) {
      sessionStorage.setItem(name, value)
    } else {
      localStorage.setItem(name, value)
    }
  },
  removeItem(name: string): void {
    localStorage.removeItem(name)
    sessionStorage.removeItem(name)
  },
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      login: async (email: string, password: string, rememberMe: boolean = true) => {
        useAuthStore.persist.clearStorage()
        useAuthStore.persist.setOptions({
          storage: createJSONStorage(() => (rememberMe ? localStorage : sessionStorage)),
        })

        const { access, refresh } = await request.login(email, password)
        set({ isAuthenticated: true, accessToken: access, refreshToken: refresh, rememberMe })

        queueTokenRefresh()
      },

      logout: async () => {
        if (refreshTimeoutId) {
          clearTimeout(refreshTimeoutId)
          refreshTimeoutId = null
        }
        set({ isAuthenticated: false, accessToken: null, refreshToken: null })
        await request.logout()
      },

      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
      isLoading: true,
      isInitializing: true,
      rememberMe: true,

      async authenticate(accessToken: string, refreshToken: string) {
        set({ isLoading: true })

        try {
          const authorized = await request.authorize(accessToken, refreshToken)

          if (authorized) {
            set({
              isAuthenticated: true,
              isLoading: false,
              isInitializing: false,
              accessToken,
              refreshToken,
            })
            queueTokenRefresh()
          } else {
            set({
              isAuthenticated: false,
              accessToken: null,
              refreshToken: null,
              isLoading: false,
            })
          }
        } catch {
          set({
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
            isLoading: false,
          })
        }
      },

      initialize: async () => {
        try {
          const { accessToken, refreshToken } = get()

          if (accessToken && refreshToken) {
            await request.authorize(accessToken, refreshToken)
            set({
              isLoading: false,
              isInitializing: false,
              isAuthenticated: true,
              accessToken,
              refreshToken,
            })
            queueTokenRefresh()
            await _token_refresher()
          } else {
            set({
              isLoading: false,
              isInitializing: false,
              isAuthenticated: false,
              accessToken: null,
              refreshToken: null,
            })
          }
        } catch {
          set({
            isLoading: false,
            isInitializing: false,
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
          })
        }
      },
    }),
    {
      name: 'auth-store',
      storage: createJSONStorage(() => loginStorage),
      onRehydrateStorage: () => {
        return (newState) => {
          newState?.initialize()
        }
      },
    },
  ),
)

async function refreshTokenWithRetry(retryCount = 0): Promise<string | undefined> {
  try {
    return await request.refreshTokens()
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      console.warn(
        `Token refresh failed (attempt ${retryCount + 1}/${MAX_RETRIES + 1}), retrying in ${RETRY_INTERVAL_MS / 1000} seconds...`,
        error,
      )

      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await refreshTokenWithRetry(retryCount + 1)
          resolve(result)
        }, RETRY_INTERVAL_MS)
      })
    } else {
      console.error(`Token refresh failed after ${MAX_RETRIES + 1} attempts`, error)
      throw error
    }
  }
}

async function _token_refresher() {
  const { logout } = useAuthStore.getState()
  try {
    const newAccessToken = await refreshTokenWithRetry()

    if (newAccessToken) {
      useAuthStore.setState({
        accessToken: newAccessToken,
      })
      queueTokenRefresh()
    } else {
      await logout()
    }
  } catch {
    await logout()
  }
}

function queueTokenRefresh() {
  const { accessToken, refreshToken } = useAuthStore.getState()

  if (!accessToken || !refreshToken) return

  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId)
    refreshTimeoutId = null
  }

  try {
    const decoded = decodeJwt(accessToken)
    const exp = decoded.exp as number

    if (!exp) return

    const now = Math.floor(Date.now() / 1000)
    const timeUntilExpiry = exp - now
    const refreshTime = Math.max(timeUntilExpiry - EXPIRY_SOON_OFFSET, 0) * 1000 // Convert to milliseconds

    refreshTimeoutId = setTimeout(_token_refresher, refreshTime)
  } catch (error) {
    console.error('Failed to schedule token refresh:', error)
  }
}
